import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Clock,
  Database,
  FileText,
  IdCard,
  Megaphone,
  MessagesSquare,
  RefreshCw,
  Send,
  SlidersHorizontal,
  SquareUser,
  UserCircle2,
  Users,
  Workflow,
} from "lucide-react";
import Illustration from "../../../assets/images/Illustration-unify.svg";
import { cn } from "../../../utils/function";
import { DASHBOARD_STATS as D, PROFILES, useUnifySetup } from "./data";
import { UnifyDiagram } from "./hero";
import { Avatar, card, fmt, linkCls, primaryBtn, secondaryBtn, Section, StatTile, Tabs } from "./ui";

// Unify Profiles dashboard (/unify/dashboard). Two states from the design:
// before setup (how it works + what's available) and after "Generate
// Unified Profiles" (live stats, last run, sample profile, recent updates).
// Sample data only — see data.ts.

const iconCls = "text-indigo-600 dark:text-indigo-400";

const HOW_STEPS = [
  { icon: Database, title: "Select Data Sources", text: "Choose the connected sources and fields you want to include in profile unification" },
  { icon: SlidersHorizontal, title: "Map Identity Fields", text: "Match identifiers like phone number, email, customer ID and name across sources." },
  { icon: Users, title: "Configure Sync, Review and Generate", text: "Check mapping coverage, resolve gaps and generate unified profiles." },
  { icon: Send, title: "Activate Baseline Profiles", text: "Make unified customer profiles available across segments, journeys, campaigns, and profile views." },
];

const EXPLAIN = [
  { title: "Resolves Customer Identity", text: "Matches records across channels using multiple identifiers to recognize the same" },
  { title: "Creates a unified customer record", text: "Combines data from CRM, broadcasts, conversations and automations into a single profile" },
  { title: "Enriches profile with connected systems", text: "Incorporates data from your connected business systems to add more context." },
  { title: "Keeps profiles fresh over time", text: "Automatically updates profiles as new data arrives from ConvoHub and connected sources." },
];

const ExploreCard: React.FC<{ live: boolean }> = ({ live }) => {
  const navigate = useNavigate();
  return (
    <div className={cn(card, "p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-6")}>
      <div className="flex-1 min-w-0">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{live ? "Explore all unified customer profiles" : "Explore unified customer profiles"}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Search, view and analyse enriched customer profiles, source contribution, match confidence and more.</p>
        <p className="mt-4 text-xl text-gray-900 dark:text-gray-100">{live ? fmt(D.unified) : 0}</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">Available profiles to explore</p>
        <button type="button" onClick={() => navigate(live ? "/unify/profiles" : "/unify/setup")} className={cn(primaryBtn, "mt-4")}>
          {live ? "Open Profile Explorer" : "Start Unification"}
        </button>
      </div>
      <img src={Illustration} alt="" className="w-40 sm:w-48 self-center shrink-0" />
    </div>
  );
};

// ── Before setup ────────────────────────────────────────────────────────────

const EmptyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const fields = ["Full Name", "Customer ID", "Email", "Phone Number:", "Location:", "Customer Since:", "Lead Stage:", "Source:", "Last Interaction Date", "Last Interaction Channel"];
  return (
    <>
      <div className={cn(card, "p-4 sm:p-5")}>
        <div className="flex flex-col xl:flex-row gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 flex-1 lg:divide-x divide-gray-200 dark:divide-gray-800">
            {HOW_STEPS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="px-3 py-3 text-center">
                <Icon size={20} className={cn("mx-auto", iconCls)} />
                <p className="mt-2 text-[15px] font-medium text-gray-900 dark:text-gray-100">{title}</p>
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <div className="hidden md:flex justify-center items-center shrink-0 scale-90">
            <UnifyDiagram />
          </div>
        </div>
        <button type="button" onClick={() => navigate("/unify/setup")} className={cn(primaryBtn, "mt-4")}>
          Setup Baseline Profile
        </button>
      </div>

      <Section title="Available for Unification" subtitle="Your native ConvoHub CRM, broadcast events and automation data can be used as the baseline for a unified customer profile">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <StatTile icon={<Users size={18} className={iconCls} />} label="ConvoHub Native Profiles" value={fmt(30000)} description="Native customer, broadcast and automation data already available" />
          <StatTile
            icon={<Database size={18} className={iconCls} />}
            label="Connected Enrichment Sources"
            value={4}
            footer={
              <div className="flex flex-wrap gap-1.5">
                {["WebSDK", "Meta", "Google Analytics"].map((t) => (
                  <span key={t} className="text-xs bg-indigo-50 dark:bg-indigo-500/15 text-gray-700 dark:text-gray-300 rounded px-1.5 py-0.5">{t}</span>
                ))}
              </div>
            }
          />
          <StatTile
            icon={<IdCard size={18} className={iconCls} />}
            label="Matching Identifiers Available"
            value={2}
            footer={
              <div className="flex flex-wrap gap-1.5">
                {["Email", "External IDs"].map((t) => (
                  <span key={t} className="text-xs bg-indigo-50 dark:bg-indigo-500/15 text-gray-700 dark:text-gray-300 rounded px-1.5 py-0.5">{t}</span>
                ))}
              </div>
            }
          />
          <StatTile icon={<RefreshCw size={18} className={iconCls} />} label="Last Refresh" value="Not started" valueClass="text-gray-400 dark:text-gray-500" description="Runs will appear after setup" />
        </div>
      </Section>

      <Section title="Baseline Profile Preview" subtitle="Setup a baseline customer profile from existing data sources">
        <div className={card}>
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-200 dark:border-gray-800">
            <UserCircle2 size={40} className="text-gray-400 shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-gray-900 dark:text-gray-100">No baseline profiles available yet</p>
              <p className="text-[11px] text-gray-500">Activate your baseline profile to start building unified customer profile</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <dl className="px-4 py-3 space-y-2.5">
              {fields.map((f) => (
                <div key={f} className="flex items-center justify-between gap-4 sm:justify-start">
                  <dt className="w-48 shrink-0 text-sm text-gray-600 dark:text-gray-400">{f}</dt>
                  <dd className="h-3 w-24 rounded-full bg-indigo-50 dark:bg-gray-800" />
                </div>
              ))}
            </dl>
            <div className="flex flex-col items-center justify-center text-center px-6 py-8">
              <SquareUser size={48} className="text-indigo-600" />
              <p className="mt-3 text-[15px] font-medium text-gray-900 dark:text-gray-100">Your sample baseline customer profile will appear here</p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 max-w-sm">
                Once you connect data sources and activate baseline profile setup, ConvoHub will start showing unified customer details here.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4">
        <div className={cn(card, "p-4")}>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Included Native Data</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 mb-3">Activate ConvoHub native data to include it in your baseline customer profile</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile icon={<FileText size={18} className={iconCls} />} label="ConvoCRM Records" value={fmt(D.native.crm)} description="Customer records currently available from ConvoCRM." />
            <StatTile icon={<Megaphone size={18} className={iconCls} />} label="Broadcast Events" value={D.native.broadcast} description="Campaign and broadcast interactions captured for customer profiles." />
            <StatTile icon={<MessagesSquare size={18} className={iconCls} />} label="Conversation Events" value={D.native.conversation} description="Customer conversation activity captured across supported channels." />
            <StatTile icon={<Workflow size={18} className={iconCls} />} label="Automation Events" value={D.native.automation} description="Workflow and journey events generated from ConvoHub automations." />
          </div>
        </div>
        <div className={cn(card, "p-4 flex flex-col")}>
          <p className="flex items-center gap-2 text-[15px] text-gray-900 dark:text-gray-100">
            <Clock size={18} className={iconCls} /> Latest Run
          </p>
          <p className="mt-3 text-xl text-gray-500">No runs yet</p>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">Your first baseline profile run will appear here after setup</p>
          <button type="button" onClick={() => navigate("/unify/setup")} className={cn(secondaryBtn, "mt-auto justify-between w-full")}>
            View Setup Checklist <ArrowRight size={16} />
          </button>
        </div>
      </div>

      <Section title="What this profile does" subtitle="The baseline profile combines your native ConvoHub data and enriches it with connected systems to create a trusted, unified customer profile">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {EXPLAIN.map((e) => (
            <div key={e.title} className="rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 p-4">
              <p className="text-[15px] font-medium text-gray-900 dark:text-gray-100">{e.title}</p>
              <p className="mt-2 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">{e.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <ExploreCard live={false} />
    </>
  );
};

// ── After setup ─────────────────────────────────────────────────────────────

const Donut: React.FC = () => {
  const r = 46;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const total = D.health.reduce((n, h) => n + h.pct, 0);
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
      <circle cx="60" cy="60" r={r} fill="none" stroke="#3F3F46" strokeWidth="16" />
      {D.health.map((h) => {
        const len = (h.pct / total) * c;
        const el = <circle key={h.label} cx="60" cy="60" r={r} fill="none" stroke={h.color} strokeWidth="16" strokeDasharray={`${len - 2} ${c - len + 2}`} strokeDashoffset={-offset} />;
        offset += len;
        return el;
      })}
    </svg>
  );
};

const LiveDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Basic Info");
  const sample = PROFILES[1];
  const basic: [string, React.ReactNode][] = [
    ["Full Name", "John Doe"],
    ["Customer ID", <span className="text-indigo-700 dark:text-indigo-400">83947494</span>],
    ["KYC Status", <span className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">N.A</span>],
    ["Email", sample.email],
    ["Gender", "Male"],
    ["Age group", "18-24"],
    ["Interests", <span className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">Movies</span>],
    ["Location:", "Bengaluru, K.A."],
    ["Income band", "High Income Band"],
    ["Credit Score", <span className="text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">810</span>],
  ];

  return (
    <>
      <div className={cn(card, "p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-6")}>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{fmt(D.available)} available baseline profiles</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Your unified baseline profiles are live and ready to be enriched. Connect behavioural data from your apps, web and engagement channels to get a more complete view of your customers
          </p>
          <button type="button" onClick={() => navigate("/unify/setup")} className={cn(primaryBtn, "mt-6")}>
            Enhance Profiles
          </button>
        </div>
        <div className="hidden md:flex justify-center shrink-0 scale-90">
          <UnifyDiagram />
        </div>
      </div>

      <Section title="Unified Profiles" subtitle="Monitor your unified customer foundation, source coverage, matching and refresh status">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <StatTile icon={<Users size={18} className={iconCls} />} label="Unified Profiles" value={fmt(D.unified)} description="Native customer, broadcast and automation data already available" />
          <StatTile icon={<IdCard size={18} className={iconCls} />} label="Profiles Enriched" value={fmt(D.enriched)} description="Profile containing data from more than one source" />
          <StatTile icon={<Database size={18} className={iconCls} />} label="Connected Sources" value={D.sources} description="Sources currently contributing to the baseline profile" />
          <StatTile icon={<IdCard size={18} className={iconCls} />} label="Match Rate" value={`${D.matchRate}%`} description="Records successfully linked to an existing customer profile" />
          <StatTile icon={<AlertCircle size={18} className="text-orange-500" />} label="Unmatched" value={fmt(D.unmatched)} description="No identifiers selected" />
          <StatTile
            icon={<RefreshCw size={18} className={iconCls} />}
            label="Last Refresh"
            value="1 min ago"
            valueClass="text-gray-500"
            footer={
              <button type="button" onClick={() => navigate("/unify/merge-logs")} className={cn(linkCls, "text-xs inline-flex items-center gap-1")}>
                View Logs <ArrowRight size={12} />
              </button>
            }
          />
        </div>
      </Section>

      <div className={cn(card, "p-4")}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Last Run</h2>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {D.lastRun.at} <span className="mx-1.5">•</span> Next Run <span className="ml-1">{D.lastRun.next}</span>
            </p>
          </div>
          <button type="button" onClick={() => navigate("/unify/merge-logs")} className={cn(linkCls, "text-xs inline-flex items-center gap-1")}>
            View Logs <ArrowRight size={12} />
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          {[
            { icon: <FileText size={18} className={iconCls} />, label: "Records Processed", value: fmt(D.lastRun.processed) },
            { icon: <IdCard size={18} className={iconCls} />, label: "Profiles Enriched", value: D.lastRun.enriched },
            { icon: <IdCard size={18} className={iconCls} />, label: "Match Rate", value: `${D.lastRun.matchRate}%` },
            { icon: <AlertTriangle size={18} className="text-orange-500" />, label: "Unmatched", value: D.lastRun.unmatched },
          ].map((t) => (
            <button key={t.label} type="button" onClick={() => navigate("/unify/merge-logs")} className={cn(card, "p-4 text-left hover:border-indigo-300")}>
              <p className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
                {t.icon}
                {t.label}
              </p>
              <p className="mt-3 text-xl font-medium text-indigo-700 dark:text-indigo-400 underline underline-offset-4">{t.value}</p>
            </button>
          ))}
        </div>
        <button type="button" onClick={() => toast("Run frequency can be changed in setup step 3")} className={cn(primaryBtn, "mt-3")}>
          Schedule Run Frequency
        </button>
      </div>

      <Section title="Baseline Profile Preview" subtitle="Setup a baseline customer profile from existing data sources">
        <div className={card}>
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-200 dark:border-gray-800">
            <UserCircle2 size={40} className="text-gray-400 shrink-0" />
            <div>
              <p className="text-[15px] font-medium text-gray-900 dark:text-gray-100">Sample Profile</p>
              <p className="text-[11px] text-gray-500">+91 97888 82888</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-gray-200 dark:divide-gray-800">
            <div className="p-4">
              <Tabs tabs={["Basic Info", "Traits", "Activity", "Identities", "Merge History"]} active={tab} onChange={setTab} />
              <dl className="mt-3 space-y-2.5">
                {tab === "Basic Info" ? (
                  basic.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4 text-sm">
                      <dt className="text-gray-600 dark:text-gray-400">{k}</dt>
                      <dd className="text-gray-900 dark:text-gray-100 text-right break-all">{v}</dd>
                    </div>
                  ))
                ) : (
                  <p className="py-8 text-center text-sm text-gray-500">
                    Open a profile to see its {tab.toLowerCase()}.{" "}
                    <button type="button" onClick={() => navigate("/unify/unified-profiles/john-doe")} className={linkCls}>
                      View John Doe
                    </button>
                  </p>
                )}
              </dl>
            </div>
            <div className="p-4">
              <p className="text-[15px] text-gray-900 dark:text-gray-100">Profile Health</p>
              <div className="mt-4 flex flex-col sm:flex-row items-center gap-6">
                <div className="flex flex-col items-center">
                  <Donut />
                  <p className="mt-2 text-xl text-gray-900 dark:text-gray-100">{fmt(D.enriched)}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Profiles</p>
                </div>
                <ul className="flex-1 w-full space-y-1.5 text-xs">
                  {D.health.map((h) => (
                    <li key={h.label} className="grid grid-cols-[1fr_auto_auto] gap-4 items-center text-gray-700 dark:text-gray-300">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: h.color }} />
                        {h.label}
                      </span>
                      <span>{h.pct}%</span>
                      <span className="text-gray-500 w-12 text-right">{fmt(h.count)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={cn(card, "p-4")}>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recently Updated</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">These are your recently updated profiles</p>
          <ul className="mt-3 space-y-3">
            {PROFILES.slice(1).map((p) => (
              <li key={p.id} className="grid grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_auto_auto] items-center gap-3">
                <Avatar name={p.name} />
                <div className="min-w-0">
                  <p className="text-[15px] text-gray-900 dark:text-gray-100 truncate">{p.name}</p>
                  <p className="text-[11px] text-gray-500">{p.updated}</p>
                </div>
                <span className="hidden sm:block text-sm text-gray-800 dark:text-gray-200 mr-6">{p.sources} Sources</span>
                <button type="button" onClick={() => navigate(`/unify/unified-profiles/${p.id}`)} className={cn(linkCls, "text-xs")}>
                  View Profile
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className={cn(card, "p-4")}>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Make your profiles richer</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Connect additional sources to create more complete and actionable customer profiles</p>
          <ul className="mt-4 space-y-3">
            {["Engagement Enrichment", "Behavioural Enrichment"].map((t) => (
              <li key={t} className="flex items-center justify-between gap-3">
                <span className="text-[15px] text-gray-900 dark:text-gray-100">{t}</span>
                <button type="button" onClick={() => navigate("/unify/setup")} className={secondaryBtn}>
                  Set up
                </button>
              </li>
            ))}
            <li className="flex items-center justify-between gap-3">
              <span className="text-[15px] text-gray-900 dark:text-gray-100">External Engagements</span>
              <span className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 rounded px-1.5 py-0.5">Available Soon</span>
            </li>
          </ul>
        </div>
      </div>

      <ExploreCard live />
    </>
  );
};

const UnifyPage: React.FC = () => {
  const setup = useUnifySetup();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className="space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-medium text-gray-900 dark:text-gray-100">Unify Profiles</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Resolve identities and create real-time unified customer profiles from CRM, telco and event data</p>
        </div>
        {setup.generatedAt ? <LiveDashboard /> : <EmptyDashboard />}
      </div>
    </div>
  );
};

export default UnifyPage;
